import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Copy, ExternalLink, Sparkles, Upload, Plus, X, Share2, Play, Clapperboard, Network, Star,
  UserCircle2, Users, ShieldCheck, Banknote, Flag } from "lucide-react";
import { useNavigate } from "react-router-dom";
import client from "@/lib/api";
import ProfileDirectory from "@/pages/ProfileDirectory";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fileUrl = (p) => p?.startsWith("http") ? p : (p ? `${BACKEND_URL}/api/files/${p}` : "");
const MODES = [
  { key: "viewer", label: "Viewer", sub: "Watch & discover", icon: Play, to: "/app/watch" },
  { key: "streamer", label: "Streamer", sub: "Upload & sell", icon: Clapperboard, to: "/app/studio" },
  { key: "artist", label: "Artist", sub: "Network & hire", icon: Network, to: "/app/network" },
  { key: "influencer", label: "Influencer", sub: "Deals & pipeline", icon: Star, to: "/app/factory" },
];

/* ------------------------------------ Settings + Parental + Report + Flag ------------------------------------ */
function SettingsPanel() {
  const { user, refresh } = useAuth();
  const [parental, setParental] = useState(user?.parental_controls || { enabled: false, pin: "", max_rating: "UA" });
  const [rep, setRep] = useState({ target: "", reason: "spam", details: "" });
  const [content, setContent] = useState([]);
  const [flag, setFlag] = useState({ content_id: "", reason: "inappropriate", details: "" });

  useEffect(() => { api.watchFeed("", "").then(r => setContent(r.items || [])).catch(() => {}); }, []);

  const saveParental = async (next) => {
    setParental(next);
    try { await api.updateProfile({ parental_controls: next }); await refresh(); toast.success("Parental controls saved"); }
    catch { toast.error("Could not save"); }
  };
  const sendReport = async () => {
    if (!rep.target.trim()) return toast.error("What are you reporting?");
    try { await api.report(rep); setRep({ target: "", reason: "spam", details: "" }); toast.success("Report submitted — thank you"); }
    catch { toast.error("Could not submit report"); }
  };
  const sendFlag = async () => {
    if (!flag.content_id) return toast.error("Pick a title to flag");
    try { await api.flagContent(flag.content_id, { reason: flag.reason, details: flag.details }); setFlag({ content_id: "", reason: "inappropriate", details: "" }); toast.success("Content flagged for review"); }
    catch { toast.error("Could not flag content"); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="settings-panel">
      <div className="border border-white/10 bg-[#0C0C0C] rounded-lg p-6 space-y-4" data-testid="parental-settings">
        <div className="mono text-kinetic flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> PARENTAL SETTINGS</div>
        <div className="flex items-center justify-between">
          <span className="font-sans">Enable parental controls</span>
          <Switch data-testid="parental-enable" checked={!!parental.enabled} onCheckedChange={(v) => saveParental({ ...parental, enabled: v })} />
        </div>
        <div>
          <Label className="mono text-white/60">4-digit PIN</Label>
          <Input data-testid="parental-pin" type="password" maxLength={4} value={parental.pin || ""} disabled={!parental.enabled}
            onChange={(e) => setParental(p => ({ ...p, pin: e.target.value.replace(/\D/g, "").slice(0, 4) }))}
            onBlur={() => parental.enabled && saveParental(parental)} placeholder="••••" className="bg-black border-white/15 rounded-sm mt-1 w-32" />
        </div>
        <div>
          <Label className="mono text-white/60">Maximum content rating</Label>
          <Select value={parental.max_rating || "UA"} onValueChange={(v) => saveParental({ ...parental, max_rating: v })}>
            <SelectTrigger data-testid="parental-rating" className="bg-black border-white/15 rounded-sm mono mt-1" disabled={!parental.enabled}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0C0C0C] border-white/15 text-white">
              {[["U", "U · Universal"], ["UA", "U/A · Parental guidance"], ["A", "A · Adults only"]].map(([k, l]) => <SelectItem key={k} value={k} className="mono">{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-6">
        <div className="border border-white/10 bg-[#0C0C0C] rounded-lg p-6 space-y-3" data-testid="report-panel">
          <div className="mono text-kinetic flex items-center gap-2"><Flag className="w-4 h-4" /> REPORT</div>
          <Input data-testid="report-target" value={rep.target} onChange={(e) => setRep(s => ({ ...s, target: e.target.value }))} placeholder="User, channel or issue" className="bg-black border-white/15 rounded-sm" />
          <Select value={rep.reason} onValueChange={(v) => setRep(s => ({ ...s, reason: v }))}>
            <SelectTrigger data-testid="report-reason" className="bg-black border-white/15 rounded-sm mono"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0C0C0C] border-white/15 text-white">
              {["spam", "harassment", "copyright", "scam", "other"].map(r => <SelectItem key={r} value={r} className="mono">{r.toUpperCase()}</SelectItem>)}
            </SelectContent>
          </Select>
          <Textarea data-testid="report-details" value={rep.details} onChange={(e) => setRep(s => ({ ...s, details: e.target.value }))} placeholder="Details…" className="bg-black border-white/15 rounded-sm min-h-16" />
          <Button data-testid="report-submit" onClick={sendReport} className="kinetic-btn rounded-sm w-full">Submit report</Button>
        </div>

        <div className="border border-white/10 bg-[#0C0C0C] rounded-lg p-6 space-y-3" data-testid="flag-panel">
          <div className="mono text-kinetic flex items-center gap-2"><Flag className="w-4 h-4" /> FLAG A CONTENT</div>
          <Select value={flag.content_id} onValueChange={(v) => setFlag(s => ({ ...s, content_id: v }))}>
            <SelectTrigger data-testid="flag-content" className="bg-black border-white/15 rounded-sm mono"><SelectValue placeholder="SELECT A TITLE" /></SelectTrigger>
            <SelectContent className="bg-[#0C0C0C] border-white/15 text-white max-h-64">
              {content.map(c => <SelectItem key={c.id} value={c.id} className="mono">{c.title}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={flag.reason} onValueChange={(v) => setFlag(s => ({ ...s, reason: v }))}>
            <SelectTrigger data-testid="flag-reason" className="bg-black border-white/15 rounded-sm mono"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0C0C0C] border-white/15 text-white">
              {["inappropriate", "violence", "copyright", "misleading", "other"].map(r => <SelectItem key={r} value={r} className="mono">{r.toUpperCase()}</SelectItem>)}
            </SelectContent>
          </Select>
          <Textarea data-testid="flag-details" value={flag.details} onChange={(e) => setFlag(s => ({ ...s, details: e.target.value }))} placeholder="Why are you flagging this?" className="bg-black border-white/15 rounded-sm min-h-16" />
          <Button data-testid="flag-submit" onClick={sendFlag} className="kinetic-btn rounded-sm w-full">Flag content</Button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------ Box Office (streamer bank/payout) ------------------------------------ */
function BoxOfficePanel() {
  const { user, refresh } = useAuth();
  const [b, setB] = useState({
    account_holder: "", bank_name: "", account_number: "", ifsc: "", upi: "", pan: "", payout_method: "bank",
    ...(user?.bank_details || {}),
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setB(s => ({ ...s, [k]: v }));
  const save = async () => {
    setBusy(true);
    try { await api.updateProfile({ bank_details: b }); await refresh(); toast.success("Payout details saved"); }
    catch { toast.error("Could not save"); } finally { setBusy(false); }
  };
  return (
    <div className="max-w-2xl space-y-5" data-testid="box-office-panel">
      <div className="border border-white/10 bg-[#0C0C0C] rounded-lg p-6 space-y-4">
        <div className="mono text-kinetic flex items-center gap-2"><Banknote className="w-4 h-4" /> BOX OFFICE · PAYOUT & BANK DETAILS</div>
        <p className="text-white/50 mono">Where your box-office earnings (PPV, rentals, subscriptions, ticket sales) get paid out.</p>
        <div>
          <Label className="mono text-white/60">Payout method</Label>
          <Select value={b.payout_method || "bank"} onValueChange={(v) => set("payout_method", v)}>
            <SelectTrigger data-testid="bank-method" className="bg-black border-white/15 rounded-sm mono mt-1"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0C0C0C] border-white/15 text-white">
              {[["bank", "BANK TRANSFER"], ["upi", "UPI"], ["paypal", "PAYPAL"]].map(([k, l]) => <SelectItem key={k} value={k} className="mono">{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label className="mono text-white/60">Account holder name</Label>
            <Input data-testid="bank-holder" value={b.account_holder} onChange={(e) => set("account_holder", e.target.value)} className="bg-black border-white/15 rounded-sm mt-1" /></div>
          <div><Label className="mono text-white/60">Bank name</Label>
            <Input data-testid="bank-name" value={b.bank_name} onChange={(e) => set("bank_name", e.target.value)} className="bg-black border-white/15 rounded-sm mt-1" /></div>
          <div><Label className="mono text-white/60">Account number</Label>
            <Input data-testid="bank-account" value={b.account_number} onChange={(e) => set("account_number", e.target.value)} className="bg-black border-white/15 rounded-sm mt-1" /></div>
          <div><Label className="mono text-white/60">IFSC / routing</Label>
            <Input data-testid="bank-ifsc" value={b.ifsc} onChange={(e) => set("ifsc", e.target.value)} className="bg-black border-white/15 rounded-sm mt-1" /></div>
          <div><Label className="mono text-white/60">UPI ID</Label>
            <Input data-testid="bank-upi" value={b.upi} onChange={(e) => set("upi", e.target.value)} placeholder="name@bank" className="bg-black border-white/15 rounded-sm mt-1" /></div>
          <div><Label className="mono text-white/60">PAN / Tax ID</Label>
            <Input data-testid="bank-pan" value={b.pan} onChange={(e) => set("pan", e.target.value)} className="bg-black border-white/15 rounded-sm mt-1" /></div>
        </div>
        <div className="flex justify-end">
          <Button data-testid="bank-save" onClick={save} disabled={busy} className="kinetic-btn rounded-sm h-11 px-8">{busy ? "Saving…" : "Save payout details"}</Button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------ Main ------------------------------------ */
export default function MyProfile() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [uploadingKey, setUploadingKey] = useState(null);
  const [tab, setTab] = useState("profile");

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "", slug: user.slug || "", headline: user.headline || "", bio: user.bio || "",
        cover_image: user.cover_image || "", picture: user.picture || "",
        instagram: user.instagram || "", youtube: user.youtube || "", tiktok: user.tiktok || "",
        accepting_bookings: user.accepting_bookings ?? true, rate_cards: user.rate_cards || [],
        availability: user.availability || [], deposit_pct: user.deposit_pct ?? 0, showcase: user.showcase || [],
      });
    }
  }, [user]);

  if (!form) return <div className="mono text-white/50">LOADING…</div>;

  const isStreamer = user?.mode === "streamer";
  const TABS = [
    ["profile", "Profile", UserCircle2],
    ["directory", "Directory", Users],
    ["settings", "Settings", ShieldCheck],
    ...(isStreamer ? [["boxoffice", "Box Office", Banknote]] : []),
  ];

  const upload = async (field, file) => {
    if (!file) return;
    setUploadingKey(field);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await client.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } }).then(r => r.data);
      setForm(f => ({ ...f, [field]: r.path }));
      toast.success("Uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploadingKey(null); }
  };
  const generateSlug = async () => {
    try { const r = await client.post("/me/profile/auto-slug").then(r => r.data); setForm(f => ({ ...f, slug: r.slug })); await refresh(); toast.success(`Slug set: @${r.slug}`); }
    catch { toast.error("Could not generate"); }
  };
  const save = async () => {
    try { await client.patch("/me/profile", form).then(r => r.data); await refresh(); toast.success("Profile saved"); }
    catch (e) { toast.error(e.response?.data?.detail === "slug taken" ? "That handle is taken" : "Could not save"); }
  };

  const publicUrl = form.slug ? `${window.location.origin}/u/${form.slug}` : null;
  const shareUrl = form.slug ? `${BACKEND_URL}/api/og/${form.slug}` : null;
  const copy = (val, label) => { navigator.clipboard.writeText(val); toast.success(`${label} copied`); };
  const setCard = (i, key, val) => setForm(f => ({ ...f, rate_cards: f.rate_cards.map((c, idx) => idx === i ? { ...c, [key]: val } : c) }));
  const addCard = () => setForm(f => ({ ...f, rate_cards: [...f.rate_cards, { service: "", price_range: "" }] }));
  const removeCard = (i) => setForm(f => ({ ...f, rate_cards: f.rate_cards.filter((_, idx) => idx !== i) }));

  return (
    <div className="space-y-8" data-testid="my-profile">
      <div>
        <div className="mono text-white/40">{(user?.mode || "viewer").toUpperCase()} · PROFILE</div>
        <h1 className="text-4xl font-extrabold mt-2">Your INDIEWOOD account</h1>
        <p className="text-white/60 mt-2 max-w-2xl">Manage your public page, discover the directory, set your controls{isStreamer ? " and your box-office payouts" : ""}.</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 flex-wrap">
        {TABS.map(([k, l, Icon]) => (
          <button key={k} data-testid={`profile-tab-${k}`} onClick={() => setTab(k)}
            className={`mono px-4 h-9 rounded-sm border inline-flex items-center gap-2 ${tab === k ? "border-kinetic text-kinetic bg-kinetic/10" : "border-white/15 text-white/55 hover:text-white"}`}>
            <Icon className="w-3.5 h-3.5" /> {l}
          </button>
        ))}
      </div>

      {tab === "directory" && <ProfileDirectory />}
      {tab === "settings" && <SettingsPanel />}
      {tab === "boxoffice" && <BoxOfficePanel />}

      {tab === "profile" && (
        <div className="space-y-8 max-w-4xl">
          {/* Login-as mode picker */}
          <div className="border border-white/10 bg-[#0C0C0C] p-5" data-testid="mode-picker">
            <div className="mono text-kinetic mb-3">LOG IN AS · JUMP TO YOUR SPACE</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {MODES.map(m => (
                <button key={m.key} data-testid={`mode-${m.key}`}
                  onClick={async () => { try { await api.setMode(m.key); await refresh(); localStorage.setItem("iw_mode_chosen", "1"); } catch { /* ignore */ } toast.success(`Entering as ${m.label}`); navigate(m.to); }}
                  className={`border rounded-lg p-4 text-left transition ${user?.mode === m.key ? "border-kinetic bg-kinetic/10" : "border-white/10 hover:border-white/30"}`}>
                  <m.icon className="w-5 h-5 text-kinetic" />
                  <div className="font-sans font-bold mt-2">{m.label}</div>
                  <div className="mono text-white/40 mt-0.5">{m.sub}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Public URL bar */}
          <div className="border border-white/10 bg-[#0C0C0C] p-5 flex flex-wrap items-center gap-3">
            <div className="mono text-kinetic">PUBLIC URL</div>
            {publicUrl ? (
              <>
                <code data-testid="public-url" className="font-mono text-sm bg-black px-3 py-2 border border-white/10 flex-1 min-w-0 truncate">{publicUrl}</code>
                <Button data-testid="copy-url" onClick={() => copy(publicUrl, "Profile link")} variant="outline" className="rounded-sm border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white"><Copy className="w-4 h-4 mr-2"/>Copy</Button>
                <Button data-testid="copy-share-url" onClick={() => copy(shareUrl, "Share link")} variant="outline" className="rounded-sm border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white"><Share2 className="w-4 h-4 mr-2"/>Share (unfurls)</Button>
                <a href={publicUrl} target="_blank" rel="noreferrer">
                  <Button data-testid="open-public" variant="outline" className="rounded-sm border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white"><ExternalLink className="w-4 h-4 mr-2"/>Open</Button>
                </a>
              </>
            ) : (
              <Button data-testid="auto-slug-btn" onClick={generateSlug} className="kinetic-btn rounded-sm"><Sparkles className="w-4 h-4 mr-2"/>Generate handle</Button>
            )}
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <div className="border border-white/10 bg-[#0C0C0C] p-6 space-y-4">
              <div>
                <Label className="mono text-white/60">Display name</Label>
                <Input data-testid="prof-name" value={form.name} onChange={(e) => setForm(f => ({...f, name: e.target.value}))} className="bg-black border-white/15 rounded-sm mt-1"/>
              </div>
              <div>
                <Label className="mono text-white/60">Handle / slug</Label>
                <Input data-testid="prof-slug" value={form.slug} onChange={(e) => setForm(f => ({...f, slug: e.target.value}))} placeholder="your-handle" className="bg-black border-white/15 rounded-sm mt-1"/>
                <div className="mono text-white/30 mt-1">Lowercase letters, numbers, dashes</div>
              </div>
              <div>
                <Label className="mono text-white/60">Headline</Label>
                <Input data-testid="prof-headline" value={form.headline} onChange={(e) => setForm(f => ({...f, headline: e.target.value}))} placeholder="Director · Branded films + docs" className="bg-black border-white/15 rounded-sm mt-1"/>
              </div>
              <div>
                <Label className="mono text-white/60">Bio</Label>
                <Textarea data-testid="prof-bio" value={form.bio} onChange={(e) => setForm(f => ({...f, bio: e.target.value}))} className="bg-black border-white/15 rounded-sm mt-1 min-h-32"/>
              </div>
              <div>
                <Label className="mono text-white/60">Cover image</Label>
                <div className="flex items-center gap-3 mt-1">
                  {form.cover_image && <img src={fileUrl(form.cover_image)} alt="cover" className="w-20 h-12 object-cover border border-white/10"/>}
                  <label className="cursor-pointer">
                    <input data-testid="upload-cover" type="file" accept="image/*" className="hidden" onChange={(e) => upload("cover_image", e.target.files[0])}/>
                    <span className="mono inline-flex items-center gap-2 px-3 h-9 border border-white/20 hover:border-kinetic transition rounded-sm">
                      <Upload className="w-3.5 h-3.5"/>{uploadingKey === "cover_image" ? "UPLOADING…" : "UPLOAD"}
                    </span>
                  </label>
                  {form.cover_image && <button onClick={() => setForm(f => ({...f, cover_image: ""}))} className="mono text-white/40 hover:text-coral">CLEAR</button>}
                </div>
              </div>
              <div>
                <Label className="mono text-white/60">Avatar</Label>
                <div className="flex items-center gap-3 mt-1">
                  {form.picture && <img src={fileUrl(form.picture)} alt="avatar" className="w-12 h-12 rounded-full object-cover border border-white/10"/>}
                  <label className="cursor-pointer">
                    <input data-testid="upload-avatar" type="file" accept="image/*" className="hidden" onChange={(e) => upload("picture", e.target.files[0])}/>
                    <span className="mono inline-flex items-center gap-2 px-3 h-9 border border-white/20 hover:border-kinetic transition rounded-sm">
                      <Upload className="w-3.5 h-3.5"/>{uploadingKey === "picture" ? "UPLOADING…" : "UPLOAD"}
                    </span>
                  </label>
                </div>
              </div>
            </div>
            <div className="border border-white/10 bg-[#0C0C0C] p-6 space-y-4">
              <div>
                <Label className="mono text-white/60">Instagram handle</Label>
                <Input value={form.instagram} onChange={(e) => setForm(f => ({...f, instagram: e.target.value}))} placeholder="@yourhandle" className="bg-black border-white/15 rounded-sm mt-1"/>
              </div>
              <div>
                <Label className="mono text-white/60">YouTube URL</Label>
                <Input value={form.youtube} onChange={(e) => setForm(f => ({...f, youtube: e.target.value}))} placeholder="https://youtube.com/@you" className="bg-black border-white/15 rounded-sm mt-1"/>
              </div>
              <div>
                <Label className="mono text-white/60">TikTok handle</Label>
                <Input value={form.tiktok} onChange={(e) => setForm(f => ({...f, tiktok: e.target.value}))} placeholder="@yourhandle" className="bg-black border-white/15 rounded-sm mt-1"/>
              </div>
              <div className="flex items-center justify-between pt-2">
                <div>
                  <Label className="mono text-white/60">Accepting bookings</Label>
                  <div className="text-white/40 mono mt-1">Toggles the &quot;Book me&quot; button on your public page</div>
                </div>
                <Switch data-testid="accepting-toggle" checked={form.accepting_bookings} onCheckedChange={(v) => setForm(f => ({...f, accepting_bookings: v}))}/>
              </div>
            </div>
          </div>

          <div className="border border-white/10 bg-[#0C0C0C] p-6 space-y-3" data-testid="rate-cards-editor">
            <div className="flex items-center justify-between">
              <div>
                <div className="mono text-kinetic">RATE CARDS · ENGAGEMENT MODELS</div>
                <div className="text-white/40 mono mt-1">Mark each as client · subscription · per-day · white-label · one-off</div>
              </div>
              <Button data-testid="add-rate-card" onClick={addCard} variant="outline" className="rounded-sm border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white"><Plus className="w-4 h-4 mr-2"/>Add</Button>
            </div>
            {form.rate_cards.length === 0 && <div className="mono text-white/30 py-4 text-center">NO CARDS</div>}
            {form.rate_cards.map((c, i) => (
              <div key={i} className="grid grid-cols-[1.4fr_1.8fr_1.4fr_auto] gap-2 items-center">
                <Input data-testid={`card-service-${i}`} placeholder="Service (e.g. Hero film)" value={c.service} onChange={(e) => setCard(i, "service", e.target.value)} className="bg-black border-white/15 rounded-sm"/>
                <Input data-testid={`card-price-${i}`} placeholder="Rate (e.g. ₹15L – ₹40L)" value={c.price_range} onChange={(e) => setCard(i, "price_range", e.target.value)} className="bg-black border-white/15 rounded-sm"/>
                <select data-testid={`card-model-${i}`} value={c.model || "client"} onChange={(e) => setCard(i, "model", e.target.value)} className="bg-black border border-white/15 text-white rounded-sm h-9 px-2 mono">
                  <option value="client">CLIENT</option>
                  <option value="subscription">SUBSCRIPTION</option>
                  <option value="per-day">PER-DAY</option>
                  <option value="white-label">WHITE-LABEL</option>
                  <option value="one-off">ONE-OFF</option>
                </select>
                <button data-testid={`card-rm-${i}`} onClick={() => removeCard(i)} className="text-white/40 hover:text-coral p-2"><X className="w-4 h-4"/></button>
              </div>
            ))}
          </div>

          <div className="border border-white/10 bg-[#0C0C0C] p-6 space-y-3" data-testid="availability-editor">
            <div className="flex items-center justify-between">
              <div>
                <div className="mono text-kinetic">AVAILABILITY</div>
                <div className="text-white/40 mono mt-1">Date windows shown publicly</div>
              </div>
              <Button data-testid="add-availability" onClick={() => setForm(f => ({ ...f, availability: [...f.availability, { start: "", end: "", label: "Open", status: "open" }] }))} variant="outline" className="rounded-sm border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white"><Plus className="w-4 h-4 mr-2"/>Add</Button>
            </div>
            {form.availability.length === 0 && <div className="mono text-white/30 py-4 text-center">NO WINDOWS</div>}
            {form.availability.map((a, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center">
                <Input data-testid={`avail-start-${i}`} type="date" value={a.start} onChange={(e) => setForm(f => ({ ...f, availability: f.availability.map((x, idx) => idx === i ? { ...x, start: e.target.value } : x) }))} className="bg-black border-white/15 rounded-sm"/>
                <Input data-testid={`avail-end-${i}`} type="date" value={a.end} onChange={(e) => setForm(f => ({ ...f, availability: f.availability.map((x, idx) => idx === i ? { ...x, end: e.target.value } : x) }))} className="bg-black border-white/15 rounded-sm"/>
                <Input data-testid={`avail-label-${i}`} placeholder="Label" value={a.label} onChange={(e) => setForm(f => ({ ...f, availability: f.availability.map((x, idx) => idx === i ? { ...x, label: e.target.value } : x) }))} className="bg-black border-white/15 rounded-sm"/>
                <button data-testid={`avail-rm-${i}`} onClick={() => setForm(f => ({ ...f, availability: f.availability.filter((_, idx) => idx !== i) }))} className="text-white/40 hover:text-coral p-2"><X className="w-4 h-4"/></button>
              </div>
            ))}
          </div>

          <div className="border border-white/10 bg-[#0C0C0C] p-6" data-testid="deposit-editor">
            <div className="mono text-kinetic">BOOKING DEPOSIT %</div>
            <div className="text-white/40 mono mt-1">Brands pay this % up-front via Stripe to confirm a booking</div>
            <div className="flex flex-wrap items-center gap-2 mt-4">
              {[0, 10, 15, 25, 50].map(p => (
                <button key={p} data-testid={`deposit-preset-${p}`} onClick={() => setForm(f => ({ ...f, deposit_pct: p }))}
                  className={`mono px-3 h-9 rounded-sm border ${form.deposit_pct === p ? "border-kinetic text-kinetic bg-kinetic/10" : "border-white/15 text-white/70 hover:border-white/40"}`}>
                  {p === 0 ? "OFF" : `${p}%`}
                </button>
              ))}
              <Input data-testid="deposit-custom" type="number" min="0" max="50" value={form.deposit_pct}
                onChange={(e) => setForm(f => ({ ...f, deposit_pct: Math.max(0, Math.min(50, Number(e.target.value) || 0)) }))}
                className="bg-black border-white/15 rounded-sm w-24 ml-2" />
              <span className="mono text-white/40">CUSTOM (0–50)</span>
            </div>
          </div>

          <div className="border border-white/10 bg-[#0C0C0C] p-6 space-y-3" data-testid="showcase-editor">
            <div className="flex items-center justify-between">
              <div>
                <div className="mono text-kinetic">SHOWCASE · PREVIOUS WORK & DEMOS</div>
                <div className="text-white/40 mono mt-1">YouTube, Vimeo, Behance, Drive — any link</div>
              </div>
              <Button data-testid="add-showcase" onClick={() => setForm(f => ({ ...f, showcase: [...f.showcase, { title: "", url: "", kind: "demo" }] }))} variant="outline" className="rounded-sm border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white"><Plus className="w-4 h-4 mr-2"/>Add link</Button>
            </div>
            {form.showcase.length === 0 && <div className="mono text-white/30 py-4 text-center">NO LINKS</div>}
            {form.showcase.map((s, i) => (
              <div key={i} className="grid grid-cols-[1fr_2fr_120px_auto] gap-2 items-center">
                <Input data-testid={`sc-title-${i}`} placeholder="Title" value={s.title} onChange={(e) => setForm(f => ({ ...f, showcase: f.showcase.map((x, idx) => idx === i ? { ...x, title: e.target.value } : x) }))} className="bg-black border-white/15 rounded-sm"/>
                <Input data-testid={`sc-url-${i}`} placeholder="https://…" value={s.url} onChange={(e) => setForm(f => ({ ...f, showcase: f.showcase.map((x, idx) => idx === i ? { ...x, url: e.target.value } : x) }))} className="bg-black border-white/15 rounded-sm"/>
                <Input data-testid={`sc-kind-${i}`} placeholder="kind" value={s.kind} onChange={(e) => setForm(f => ({ ...f, showcase: f.showcase.map((x, idx) => idx === i ? { ...x, kind: e.target.value } : x) }))} className="bg-black border-white/15 rounded-sm"/>
                <button data-testid={`sc-rm-${i}`} onClick={() => setForm(f => ({ ...f, showcase: f.showcase.filter((_, idx) => idx !== i) }))} className="text-white/40 hover:text-coral p-2"><X className="w-4 h-4"/></button>
              </div>
            ))}
          </div>

          <div className="flex justify-end">
            <Button data-testid="save-profile" onClick={save} className="kinetic-btn rounded-sm h-12 px-8">Save changes</Button>
          </div>
        </div>
      )}
    </div>
  );
}
